import keyBy from "lodash/keyBy";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";

import { DatePicker } from "@bjarkehs/react-nice-dates";
import { Alert, Button, Card, Checkbox, Group, Menu, Modal, Select, Stack, Tabs, Text, TextInput } from "@mantine/core";
import { enAU } from "date-fns/locale";
import { useNavigate, useParams } from "react-router";
import { bindActionCreators } from "redux";
import * as shiftActions from "../../../data/actions/shiftActions";
import ShiftApi, { IProcessedShiftSessionTransport } from "../../../data/api/shiftsApi";
import ShiftSessionsApi from "../../../data/api/shiftSessionsApi";
import { ILoadingState, IState } from "../../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
} from "../../../types/appointments/Appointment";
import { AppointmentStatus } from "../../../types/appointments/AppointmentStatus";
import { IAssociation } from "../../../types/association/Association";
import {
  IProcessedSessionContainer,
  IShift,
} from "../../../types/shifts/Shift";
import { IVenue } from "../../../types/venue/Venue";
import { toDateTime } from "../../../util/date+helper";
import ShiftAvailabilitiesVisualiser from "../../availabilities/ShiftAvailabilitiesVisualiser";
import { getShiftManagerUrl } from "../../common/Links";
import { CenteredLoader } from "../../common/loader";
import CreateTimesheetEntry from "../../timesheet/CreateTimesheetEntry";
import ShiftEditor from "../Shifts/ShiftEditor/ShiftEditor";
import RosterGrid from "./RosterGrid";
import RosterPersons from "./RosterPersons";
import ShiftSelector, { IShiftSessionKey } from "./ShiftSelector";

interface IRosterManagerProps {
  shifts: { [shiftId: string]: IShift };
  shiftSessions: { [sessionId: string]: IProcessedSessionContainer }; // basically a filledShift
  shiftsLoading: { [shiftId: string]: ILoadingState };

  venueMap: { [venueId: string]: IVenue };
  associationsMap: { [associationId: string]: IAssociation };
  appointmentsMapByPerson: {
    [personId: string]: IAppointment<IAppointmentDetail>[];
  };

  isAdmin: boolean;

  history: any;
  actions: {
    getShiftById: (id: string, sessionId: string, gridView: string | null) => Promise<IProcessedShiftSessionTransport>;
    publishShift: Function;
    getSupervisableShifts: () => Promise<any>;
  };
}

interface IRosterManagerState {
  venueIndex: number;
  gridView: string | null;
  customRange?: {
    start: DateTime;
    finish: DateTime;
  };
  isSelectingCustomPaysheetSelection: boolean;
}

/**
 * This class manages the shift selection and the venues and passes the shift along to the venueManager
 */
const RosterManager: React.FunctionComponent<IRosterManagerProps> = (props) => {
  const [venueIndex, setVenueIndex] = React.useState(0);
  const [_gridView, setGridView] = React.useState<string | null>("venue");
  const [shiftErrorMessage, setShiftErrorMessage] = React.useState<string | null>()
  const [showExtraPadding, setShowExtraPadding] = React.useState(true);
  const [isHideAppointmentFromSheetButtonVisibile, setHideAppointmentFromSheetButtonVisibility] = React.useState(false);
  // New local UI state for bulk confirm
  const [isConfirmingRostered, setIsConfirmingRostered] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  const [customRange, setCustomRange] = React.useState<
    { start: DateTime; finish: DateTime } | undefined
  >();
  const [
    isSelectingCustomPaysheetSelection,
    setIsSelectingCustomPaysheetSelection,
  ] = React.useState(false);
  const [
    isViewingAvailabilitiesVisualiser,
    setIsViewingAvailabilitiesVisualiser,
  ] = React.useState(false);
  const [highlightProblemGames, setHighlightProblemGames] =
    React.useState(false);
  const [addTimesheetEntry, setAddTimesheetEntry] = React.useState(false);
  const [showShiftEditorModal, setShowShiftEditorModal] = React.useState(false);
  const [publishMenuOpen, setPublishMenuOpen] = React.useState(false);
  const [paysheetMenuOpen, setPaysheetMenuOpen] = React.useState(false);

  const { shiftId, sessionId } = useParams();

  const shiftSession = sessionId ? props.shiftSessions[sessionId] : null;
  const shiftsLoading = sessionId ? props.shiftsLoading[sessionId] : null;
  const shift: IShift | null = shiftId ? props.shifts[shiftId] : null;
  const association = shift?.associationId
    ? props.associationsMap[shift?.associationId]
    : null;

  const [hasElevatedShiftPrivileges, setHasElevatedShiftPrivileges] = React.useState(false);
  const [isLoadingSupervisableShifts, setIsLoadingSupervisableShifts] = React.useState(true);
  const [shiftLoadingError, setShiftLoadingError] = React.useState<string | null>(null);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!shiftSession || !shift) {
      document.title = "Stadium Supervisor - Roster";
      return;
    }

    document.title = `Stadium Supervisor - Roster ${shift.name} (${toDateTime(
      shiftSession?.session.start,
    ).toLocaleString(DateTime.DATE_SHORT)})`;
  }, [shift, shiftSession]);

  React.useEffect(() => {
    setIsLoadingSupervisableShifts(true);
    props.actions.getSupervisableShifts()
      .catch(er => {
        setIsLoadingSupervisableShifts(false);
        setShiftLoadingError(er?.message);
      })
      .finally(() => setIsLoadingSupervisableShifts(false));


    // on page load, if the shift and sessionID are already set, then load the shift
    if (shiftId && sessionId) {
      props.actions.getShiftById(shiftId, sessionId, _gridView)
        .then(shift => {
          return ShiftSessionsApi.hasElevatedShiftPrivileges('_BLANK_', shift.shifts[0].associationId)
            .then((canEdit) => setHasElevatedShiftPrivileges(canEdit));

        })
        .catch(er => setShiftErrorMessage(er.message))
    }
  }, []);

  const openPaysheet = () => {
    if (!shiftId || !sessionId)
      return alert("Failed to open paysheet, please refresh the page");
    const url = `${ShiftApi.getShiftUrlBy(shiftId, sessionId)}/paysheet/`;
    window.open(url, "__blank");
  };

  // New handler: confirm all rostered appointments for the current session
  const confirmAllRostered = async () => {
    if (!shiftId || !sessionId) return;
    try {
      setConfirmError(null);
      setIsConfirmingRostered(true);
      await ShiftApi.confirmRosteredAppointments(shiftId, sessionId);
      await props.actions.getShiftById(shiftId, sessionId, _gridView);
    } catch (er: any) {
      setConfirmError(er?.message || 'Failed to confirm rostered appointments');
    } finally {
      setIsConfirmingRostered(false);
    }
  };

  const openCustomPaysheet = () => {
    if (!shiftId || !sessionId || !customRange)
      return alert("Failed to open paysheet, please refresh the page");
    const { start, finish } = customRange;
    const url = `${ShiftApi.getShiftUrlBy(
      shiftId,
      sessionId,
    )}/paysheet/${start.toISO()}/${finish.toISO()}`;
    window.open(url, "__blank");
  };

  const openCustomPaysheetWindow = (e) => {
    if (!shiftSession) return alert("Error opening custom paysheet window");

    const range = {
      start: DateTime.now().startOf("day"),
      finish: DateTime.now().endOf("day"),
    };

    if (shiftSession) {
      if (shiftSession.session) {
        range.start = toDateTime(shiftSession.session.start);
        range.finish = toDateTime(shiftSession.session.finish);
      }
    }

    setIsSelectingCustomPaysheetSelection(true);
    setCustomRange(range);
  };

  const handleEditShiftClick = (e: React.MouseEvent) => {
    if (!shiftId) return;

    const isMiddleClick = e.button === 1;
    const isCtrlClick = (e.ctrlKey || e.metaKey) && e.button === 0;

    if (isMiddleClick || isCtrlClick) {
      window.open(getShiftManagerUrl(shiftId), '_blank');
    } else {
      setShowShiftEditorModal(true);
    }
  };

  const handleCustomDateChange = (field: "start" | "finish") => (date: Date | null) => {
    if (!date) {
      return;
    }

    setCustomRange((prev) => {
      if (!prev) {
        return prev;
      }

      const nextDate = DateTime.fromJSDate(date).set({
        hour: prev[field].hour,
        minute: prev[field].minute,
        second: prev[field].second,
        millisecond: prev[field].millisecond,
      });

      return { ...prev, [field]: nextDate };
    });
  };

  const handleCustomTimeChange = (field: "start" | "finish") => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const [hours, minutes] = value.split(":");

    if (hours == null || minutes == null) {
      return;
    }

    setCustomRange((prev) => {
      if (!prev) {
        return prev;
      }

      const next = prev[field].set({
        hour: Number(hours),
        minute: Number(minutes),
      });

      return { ...prev, [field]: next };
    });
  };

  if (isLoadingSupervisableShifts) return <CenteredLoader />;
  if (shiftLoadingError)
    return <Alert color="red">Error loading shifts: {shiftLoadingError}</Alert>;



  const selector = (
    <ShiftSelector
      shiftId={shiftId}
      sessionId={sessionId}
      mode="supervisable"
      onChange={(session: Partial<IShiftSessionKey>) => {
        if (!session || !session.shiftId) {
          navigate("/roster");
        } else if (!session.sessionId) {
          navigate(`/roster/${session.shiftId}`);
        } else {
          navigate(`/roster/${session.shiftId}/${session.sessionId}`);
          props.actions.getShiftById(
            session.shiftId,
            session.sessionId,
            _gridView,
          ).then(shift => {
            return ShiftSessionsApi.hasElevatedShiftPrivileges('_BLANK_', shift.shifts[0].associationId)
              .then((canEdit) => setHasElevatedShiftPrivileges(canEdit));
          }).catch(er => setShiftErrorMessage(er.message));
        }
      }}
    />
  );

  if (!sessionId) {
    return selector;
  }

  if (!shiftsLoading || shiftsLoading.loading) {
    return (
      <div>
        {selector}
        <CenteredLoader />
      </div>
    );
  }

  if (!shiftSession || shiftsLoading.error || shiftErrorMessage) {
    return (
      <div>
        {selector}
        <p className="text-danger">
          {shiftErrorMessage || shiftsLoading?.error || "An unknown error has occurred"}
        </p>
      </div>
    );
  }

  let venueId = shiftSession.venueIds[venueIndex]
  if (!venueId) venueId = shiftSession.venueIds[0];
  const venue = props.venueMap[venueId];
  const _venueIds = Object.keys(props.venueMap);

  // gross hardcoded zone here
  const tz = venue?.tz || "Australia/Melbourne";
  const s = toDateTime(shiftSession.session.start).setZone(tz);
  const f = toDateTime(shiftSession.session.finish).setZone(tz);
  const shiftDateString = `${s.toLocaleString(DateTime.DATE_FULL)} - ${s.toLocaleString(DateTime.TIME_SIMPLE)} to ${f.toLocaleString(DateTime.TIME_SIMPLE)}`;


  let tabView;
  let hasVenues = true;

  const gridView =
    _gridView || (shiftSession.gameMap ? "competition" : "venue");

  if (_venueIds.length == 0) {
    hasVenues = false;
    tabView = (
      <p>
        There are no venues assigned to this shift, please contact your
        adminstrator
      </p>
    );
  } else if (gridView == "competition") {
    tabView = <></>;
  } else if (_venueIds.length == 1) {
    tabView = (
      <>
        <h3>{props.venueMap[_venueIds[0]].name}</h3>
        <hr />
      </>
    );
  } else {
    tabView = (
      <Tabs value={venueIndex.toString()} onChange={(value) => setVenueIndex(parseInt(value || '0'))}>
        <Tabs.List>
          {shiftSession.venueIds.map((id, idx) => (
            <Tabs.Tab key={idx} value={idx.toString()}>
              {id in props.venueMap ? props.venueMap[id].name : "Loading"}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
    );
  }

  const shareLink =
    shiftSession.session.confirmation &&
    `/shift/published/${shiftSession.session.confirmation.token}`;
  // const shareLink = `/shift/public/${shiftId}/${sessionId}/`

  const isPublished = Boolean(
    shiftSession && shiftSession.session.confirmation,
  );
  const publishButtonText = isPublished ? "Publish changes" : "Publish roster";

  const personsToBeNotified = shiftSession.enrolments
    .filter((e) => e.ns)
    .filter((e) =>
      props.appointmentsMapByPerson[e.person]?.some(
        (a) => a.status == AppointmentStatus.rostered,
      ),
    );
  const publishNotificationText =
    personsToBeNotified?.length > 0
      ? `\n${personsToBeNotified.length} ${personsToBeNotified.length == 1 ? "person" : "people"
      } will be notified on publish.`
      : null;
  const publishText = (
    <p
      style={{ margin: "5px", overflow: "hidden" }}
    >
      {isPublished && shiftSession.session.confirmation ? (
        <>
          This roster was published at{" "}
          {toDateTime(shiftSession.session.confirmation.date).toLocaleString(
            DateTime.TIME_SIMPLE,
          )}{" "}
          on{" "}
          {toDateTime(shiftSession.session.confirmation.date).toLocaleString(
            DateTime.DATE_FULL,
          )}
          , and can be viewed publicly with the following link:{" "}
          <a href={shareLink} target="_blank" rel="noopener noreferrer">
            {shareLink}
          </a>
          {publishNotificationText}
        </>
      ) : (
        <>
          This roster has not been published:
          {publishNotificationText}
        </>
      )}
    </p>
  );
  const alertColor = isPublished ? "green" : "yellow";

  const hasData = hasVenues;
  let gameData;
  let venues;

  switch (gridView) {
    case "competition": {
      gameData = shiftSession.gameMap;
      venues = _venueIds.map((v) => props.venueMap[v]);
      break;
    }
    case "venue":
    default: {
      gameData = shiftSession.gameMapByVenue?.[venue?._id];
      venues = [venue];
      break;
    }
  }

  const personsOnShift = shiftSession.enrolments.filter((e) =>
    props.appointmentsMapByPerson[e.person]?.some(
      (ap) =>
        ap.details &&
        ap.details.shiftSession === shiftSession.session._id &&
        ap.stillAppointed,
    ),
  );

  return (
    <div>
      {selector}
      <hr />
      <p>{shiftDateString}</p>
      {!!confirmError && (
        <Alert color="red" style={{ margin: '10px' }} withCloseButton onClose={() => setConfirmError(null)}>
          {confirmError}
        </Alert>
      )}
      <Alert color={alertColor} style={{ margin: "10px", display: "flex" }}>
        {publishText}
        <Group gap={0}>

          <Button
            size="sm"
            color={alertColor === 'green' ? 'green' : 'yellow'}
            variant="outline"
            onClick={() =>
              props.actions.publishShift(shiftSession.session._id, true)
            }
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          >
            {publishButtonText}
          </Button>
          <Menu
            opened={publishMenuOpen}
            onChange={setPublishMenuOpen}
            position="bottom-end"
          >
            <Menu.Target>
              <Button
                size="sm"
                color={alertColor === 'green' ? 'green' : 'yellow'}
                variant="outline"
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none', minWidth: 'auto', padding: '0 8px' }}
              >
                ▼
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  props.actions.publishShift(shiftSession.session._id, false)
                }
              >
                Don't send notifications
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Alert>

      {tabView}
      {hasVenues && !!gameData && (
        <div style={{ display: "flex", flexDirection: "row", padding: "10px" }}>
          <div style={{ minWidth: "250px" }}>
            <Card style={{ padding: "10px", marginRight: "10px" }}>
              {alertColor == "green" && (
                <Button
                  size="sm"
                  color={alertColor}
                  variant="outline"
                  onClick={() =>
                    props.actions.publishShift(shiftSession.session._id)
                  }
                >
                  {publishButtonText}
                </Button>
              )}
              <br />
              <Group gap={0} style={{ width: "100%" }}>
                <Button
                  onClick={openPaysheet}
                  style={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  size="sm"
                  variant="outline"
                  color="blue"
                >
                  Full Paysheet
                </Button>
                <Menu
                  opened={paysheetMenuOpen}
                  onChange={setPaysheetMenuOpen}
                  position="bottom-end"
                >
                  <Menu.Target>
                    <Button
                      size="sm"
                      variant="outline"
                      color="blue"
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none', minWidth: 'auto', padding: '0 8px' }}
                    >
                      ▼
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={openCustomPaysheetWindow}
                    >
                      Custom range
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Label>CSV Exports</Menu.Label>
                    <Menu.Item
                      onClick={() =>
                        window.open(
                          ShiftApi.getShiftUrlBy(
                            shiftId,
                            sessionId,
                          ) + "/paysheetCsv/supervisors/",
                        )
                      }
                    >
                      Supervisors (CSV)
                    </Menu.Item>
                    <Menu.Item
                      onClick={() =>
                        window.open(
                          ShiftApi.getShiftUrlBy(
                            shiftId,
                            sessionId,
                          ) + "/paysheetCsv/administrators/",
                        )
                      }
                    >
                      Administrators (CSV)
                    </Menu.Item>
                    <Menu.Item
                      onClick={() =>
                        window.open(
                          ShiftApi.getShiftUrlBy(
                            shiftId,
                            sessionId,
                          ) + "/paysheetCsv/matchOfficials/",
                        )
                      }
                    >
                      Officials (CSV)
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
              <hr />
              {hasElevatedShiftPrivileges && <Button
                onMouseDown={handleEditShiftClick}
                style={{ width: "100%" }}
                size="sm"
                variant="outline"
                color="blue"
              >
                Edit shift
              </Button>}
              {hasElevatedShiftPrivileges && <Button
                style={{ width: "100%", marginTop: "10px" }}
                size="sm"
                variant="outline"
                color="blue"
                loading={isConfirmingRostered}
                onClick={confirmAllRostered}
              >
                Confirm all rostered
              </Button>}
              {/* <Button
                style={{ width: "100%", marginTop: "10px" }}
                size="sm"
                variant="outline"
                onClick={() => setAddTimesheetEntry(true)}
              >Add timesheet entry</Button> */}
              <Button
                style={{ width: "100%", marginTop: "10px" }}
                size="sm"
                variant="outline"
                color="blue"
                onClick={() => setIsViewingAvailabilitiesVisualiser(true)}
              >
                Show availabilities visualiser
              </Button>
              {props.isAdmin && (
                <Button
                  onClick={() =>
                    ShiftApi.recalculatePayments(shiftId, sessionId)
                      .then((resp) => location.reload())
                      .catch((er) => alert(er))
                  }
                  style={{ width: "100%", marginTop: "10px" }}
                  size="sm"
                  variant="outline"
                  color="orange"
                >
                  Recalculate payments
                </Button>
              )}
              <hr />
              <Checkbox
                id="highlightProblemGames-roster-manager-checkbox"
                onChange={(e) =>
                  setHighlightProblemGames(e.currentTarget.checked)
                }
                checked={highlightProblemGames}
                label="Highlight problem games"
              />
              <Checkbox
                id="showExtraPadding-roster-manager-checkbox"
                onChange={(e) =>
                  setShowExtraPadding(e.currentTarget.checked)
                }
                checked={showExtraPadding}
                label="Show extra padding"
              />
              <Checkbox
                id="showHideAppointmentButton-roster-manager-checkbox"
                onChange={(e) =>
                  setHideAppointmentFromSheetButtonVisibility(e.currentTarget.checked)
                }
                checked={isHideAppointmentFromSheetButtonVisibile}
                label={<>{isHideAppointmentFromSheetButtonVisibile ? "Show" : "Hide"} <em>Hide appointment from sheet option</em></>}
              />
              <br />
              <Stack gap="sm">
                <Text size="sm" fw={500} style={{ color: "var(--color-text-disabled)" }}>
                  Shift Grid View
                </Text>
                <Select
                  name="gridView"
                  onChange={(value) => {
                    setGridView(value);
                    const { session } = shiftSession;
                    props.actions.getShiftById(
                      session.shift,
                      session._id,
                      value,
                    );
                  }}
                  value={gridView || "venue"}
                  data={[
                    { value: "venue", label: "Venue" },
                    { value: "competition", label: "Competition" }
                  ]}
                />
              </Stack>
              <hr />
              <h5>Referees</h5>
              <RosterPersons
                referees={shiftSession.enrolments}
                gameIds={shiftSession.gameIds}
                shiftSessionId={shiftSession.session._id}
                shiftSession={shiftSession.session}
                enrolments={keyBy(shiftSession.enrolments, "person")}
                association={association!}
                refreshSession={() =>
                  props.actions.getShiftById(
                    shiftId,
                    sessionId,
                    gridView,
                  )
                }
              />
            </Card>
          </div>
          <div style={{ width: "auto" }}>
            {!venue && <p>There are no games at this venues</p>}
            {!!venue && (
              <RosterGrid
                venues={venues}
                shiftId={shiftId}
                sessionId={sessionId}
                slots={gameData}
                officials={shiftSession.enrolments}
                hideTeamNames={gridView !== "venue"}
                gridView={gridView}
                highlightProblemGames={highlightProblemGames}
                personsOnShift={personsOnShift}
                showExtraPadding={showExtraPadding}
                showHideAppointmentFromSheetButton={isHideAppointmentFromSheetButtonVisibile}
              />
            )}
          </div>
        </div>
      )}
      {isSelectingCustomPaysheetSelection && customRange && (
        <Modal
          opened={isSelectingCustomPaysheetSelection}
          onClose={() => setIsSelectingCustomPaysheetSelection(false)}
          title="Custom Range"
        >
          <Stack gap="md">
            <Stack gap="sm">
              <p>Start</p>
              <div className="nice-dates-compact">
                <DatePicker
                  date={customRange.start.toJSDate()}
                  onDateChange={handleCustomDateChange("start")}
                  locale={enAU}
                  format="yyyy-MM-dd"
                >
                  {({ inputProps, focused }) => (
                    <TextInput
                      {...inputProps}
                      name="start"
                      type="text"
                      placeholder="YYYY-MM-DD"
                      className={`input${focused ? " -focused" : ""}`}
                    />
                  )}
                </DatePicker>
              </div>
              <TextInput
                name="start"
                type="time"
                value={customRange.start.toFormat("HH:ss")}
                onChange={handleCustomTimeChange("start")}
              />
            </Stack>
            <Stack gap="sm">
              <p>Finish</p>
              <div className="nice-dates-compact">
                <DatePicker
                  date={customRange.finish.toJSDate()}
                  onDateChange={handleCustomDateChange("finish")}
                  locale={enAU}
                  format="yyyy-MM-dd"
                >
                  {({ inputProps, focused }) => (
                    <TextInput
                      {...inputProps}
                      name="finish"
                      type="text"
                      placeholder="YYYY-MM-DD"
                      className={`input${focused ? " -focused" : ""}`}
                    />
                  )}
                </DatePicker>
              </div>
              <TextInput
                name="finish"
                type="time"
                value={customRange.finish.toFormat("HH:ss")}
                onChange={handleCustomTimeChange("finish")}
              />
            </Stack>
          </Stack>
          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setIsSelectingCustomPaysheetSelection(false)}
            >
              Cancel
            </Button>
            <Button color="blue" onClick={openCustomPaysheet}>
              Open
            </Button>
          </Group>
        </Modal>
      )}
      {isViewingAvailabilitiesVisualiser && (
        <Modal
          opened={isViewingAvailabilitiesVisualiser}
          onClose={() => setIsViewingAvailabilitiesVisualiser(false)}
          title="Availabilities Visualiser"
          size="xl"
        >
          <div style={{ overflowX: "scroll" }}>
            <ShiftAvailabilitiesVisualiser
              shiftSession={shiftSession.session}
              enrolments={shiftSession.enrolments}
            />
          </div>
          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setIsViewingAvailabilitiesVisualiser(false)}
            >
              Close
            </Button>
          </Group>
        </Modal>
      )}
      {addTimesheetEntry && (
        <Modal
          opened={addTimesheetEntry}
          onClose={() => setAddTimesheetEntry(false)}
          size="lg"
        >
          <div style={{ padding: 'var(--mantine-spacing-xl)' }}>
            <CreateTimesheetEntry
              onCancel={() => setAddTimesheetEntry(false)}
              onCreated={() => {
                setAddTimesheetEntry(false);
              }}
              shiftSession={shiftSession.session._id}
              associationId={shift?.associationId}
            />
          </div>
        </Modal>
      )}

      {showShiftEditorModal && shiftId && (
        <Modal
          opened={showShiftEditorModal}
          onClose={() => setShowShiftEditorModal(false)}
          title="Edit Shift"
          size="xl"
        >
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <ShiftEditor
              shiftId={shiftId}
              onSave={() => { }}
              onCancel={() => setShowShiftEditorModal(false)}
              defaultTab="Persons"
            />
          </div>
          <Group justify="flex-end" mt="md">
            <Button color="gray" variant="default" onClick={() => setShowShiftEditorModal(false)}>
              Close
            </Button>
          </Group>
        </Modal>
      )}
    </div>
  );
};

function mapStateToProps(
  state: IState,
  props: IRosterManagerProps,
): Partial<IRosterManagerProps> {
  // return (shifts && shiftId && sessionId) ? shifts[shiftId + sessionId] : null;
  const shiftSessions = state.shiftSessions.filled;
  const shiftsLoading = state.shiftSessions.loading;
  return {
    shiftSessions,
    shiftsLoading,
    venueMap: state.venues.map,
    shifts: state.shifts.map,
    // persons: state.persons.map,
    // @ts-ignore
    isAdmin: state.persons.map[state.persons.self]?.admin,
    associationsMap: state.associations.map,
    appointmentsMapByPerson: Object.keys(state.appointments.byPerson).reduce(
      (previous, person) => ({
        ...previous,
        [person]: state.appointments.byPerson[person].map(
          (ap) => state.appointments.map[ap],
        ),
      }),
      {},
    ),
  };
}

function mapDispatchToProps(dispatch): Partial<IRosterManagerProps> {
  return {
    actions: bindActionCreators({ ...shiftActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterManager);
